import React, { useEffect, useMemo, useState } from "react";
import { RiMessageLine } from "react-icons/ri";
import { FaSearch } from "react-icons/fa";
import { Link, Outlet, useLocation } from "react-router-dom";
import { formatMoney } from "../../../utils";
import {
  useGetAdminSmsTopupRequestsQuery,
  useGetSmsMessagingOverviewQuery,
} from "../../../store/smsMessagingAdminApi";

function extractPaginated(res) {
  if (!res) {
    return { rows: [], total: 0, currentPage: 1, lastPage: 1 };
  }

  const nested = res.data;

  if (nested && Array.isArray(nested.data)) {
    return {
      rows: nested.data,
      total: nested.total ?? res.total ?? 0,
      currentPage: nested.current_page ?? res.currentPage ?? 1,
      lastPage: nested.last_page ?? res.lastPage ?? 1,
    };
  }

  if (Array.isArray(nested)) {
    return {
      rows: nested,
      total: res.total ?? nested.length,
      currentPage: res.currentPage ?? 1,
      lastPage: res.lastPage ?? 1,
    };
  }

  return { rows: [], total: 0, currentPage: 1, lastPage: 1 };
}

function RecentPendingTopupsSkeleton() {
  return (
    <ul className="space-y-2 animate-pulse" aria-busy="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <li
          key={i}
          className="flex justify-between gap-2 border-b border-gray-100 pb-2"
        >
          <span className="h-4 flex-1 max-w-[65%] bg-gray-200 rounded" />
          <span className="h-4 w-24 bg-gray-100 rounded shrink-0" />
        </li>
      ))}
    </ul>
  );
}

function CooperativeListSkeleton({ rows = 6 }) {
  return (
    <div className="divide-y divide-gray-200 animate-pulse" aria-busy="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 py-5"
        >
          <div className="space-y-2 flex-1">
            <div className="h-5 bg-gray-200 rounded w-2/3 max-w-xs" />
            <div className="h-3.5 bg-gray-100 rounded w-1/2 max-w-sm" />
            <div className="h-3 bg-gray-100 rounded w-40" />
          </div>
          <div className="text-right space-y-2 md:min-w-[140px]">
            <div className="h-7 bg-gray-200 rounded w-28 ml-auto" />
            <div className="h-3.5 bg-gray-100 rounded w-36 ml-auto" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Messaging() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("active");
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [activeTab, appliedSearch]);

  const overviewQuery = useGetSmsMessagingOverviewQuery({
    tab: activeTab,
    page,
    search: appliedSearch,
  });
  const overview = useMemo(
    () => extractPaginated(overviewQuery.data),
    [overviewQuery.data]
  );

  const topupQuery = useGetAdminSmsTopupRequestsQuery({
    page: 1,
    status: "all",
  });
  const pendingTopupQuery = useGetAdminSmsTopupRequestsQuery({
    page: 1,
    status: "PENDING",
  });
  const topupOverview = useMemo(
    () => extractPaginated(topupQuery.data),
    [topupQuery.data]
  );
  const pendingTopupOverview = useMemo(
    () => extractPaginated(pendingTopupQuery.data),
    [pendingTopupQuery.data]
  );

  const pendingTopupCount = pendingTopupOverview.total;
  const recentTopups = topupOverview.rows.slice(0, 4);

  const topupInitialLoad =
    topupQuery.isLoading || (topupQuery.isFetching && topupQuery.data === undefined);

  const isLoadingList = overviewQuery.isLoading || overviewQuery.isFetching;
  const loadError = overviewQuery.isError
    ? overviewQuery.error?.data?.message ||
      overviewQuery.error?.message ||
      "Could not load overview."
    : null;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {location.pathname === "/sms-messaging" ? (
        <>
          <div className="bg-white rounded-xl p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-2xl font-bold text-[#45464e]">SMS Messaging</h1>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/sms-messaging/delivery-log"
                className="bg-white border border-[#8b313a] text-[#8b313a] py-2 px-4 rounded-md text-sm font-medium hover:bg-gray-50 transition whitespace-nowrap"
              >
                SMS delivery log
              </Link>
              <Link
                to="/sms-messaging/platform-sms-pricing"
                className="bg-white border border-[#31778c] text-[#31778c] py-2 px-4 rounded-md text-sm font-medium hover:bg-gray-50 transition whitespace-nowrap"
              >
                Platform SMS pricing
              </Link>
              <Link
                to="/sms-messaging/messaging-providers"
                className="bg-[#31778c] text-white py-2 px-4 rounded-md text-sm font-medium hover:opacity-90 transition whitespace-nowrap"
              >
                Messaging provider settings
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-[#383838] rounded-xl p-6 relative overflow-hidden">
              <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-gray-600 opacity-30"></div>
              <div className="absolute -right-5 -top-5 w-24 h-24 rounded-full bg-gray-700 opacity-40"></div>
              <div className="flex items-center mb-6 z-10">
                <div className="bg-white p-3 rounded-full mr-4">
                  <RiMessageLine className="text-[#8b313a] text-2xl" />
                </div>
                <div>
                  <p className="text-white text-3xl font-bold min-h-[2.25rem] flex items-center">
                    {topupInitialLoad ? (
                      <span
                        className="inline-block h-9 w-20 bg-gray-500/70 rounded-md animate-pulse"
                        aria-hidden
                      />
                    ) : (
                      pendingTopupCount
                    )}
                  </p>
                  <p className="text-gray-300 text-sm">
                    Pending SMS top-up requests
                  </p>
                </div>
              </div>
              <Link
                to="/sms-messaging/top_up_orders"
                className="mt-4 bg-[#8b313a] text-white py-2 px-6 rounded-md text-sm hover:bg-red-700 transition w-fit inline-block"
              >
                View top-up history
              </Link>
            </div>

            <div className="bg-white rounded-xl p-6 flex flex-col justify-between border border-gray-100">
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  Recent SMS top-ups
                </h3>
                {topupInitialLoad ? (
                  <RecentPendingTopupsSkeleton />
                ) : recentTopups.length === 0 ? (
                  <p className="text-gray-600 text-sm">
                    No SMS top-ups yet. Cooperatives pay online; successful
                    payments credit their branch wallet automatically and appear
                    here.
                  </p>
                ) : (
                  <ul className="space-y-2 text-sm text-gray-700">
                    {recentTopups.map((row) => (
                      <li
                        key={row.id}
                        className="flex justify-between gap-2 border-b border-gray-100 pb-2"
                      >
                        <span className="font-medium truncate">
                          {row.company?.name ?? `Company #${row.company_id}`}
                          <span className="ml-2 text-xs text-gray-400 font-normal">
                            {String(row.status || "").toUpperCase()}
                          </span>
                        </span>
                        <span className="text-[#31778c] whitespace-nowrap">
                          {formatMoney(row.amount_paid, true)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <Link
                to="/sms-messaging/top_up_orders"
                className="mt-4 bg-[#8b313a] text-white py-2 px-6 rounded-md text-sm hover:bg-red-700 transition w-fit"
              >
                View all
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-6 px-6" aria-label="Tabs">
                <button
                  type="button"
                  onClick={() => setActiveTab("active")}
                  className={`pb-2 ${
                    activeTab === "active"
                      ? "text-[#8B313A] font-semibold border-b-2 border-[#8B313A]"
                      : "text-gray-500"
                  }`}
                >
                  Active subscription
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("expired")}
                  className={`pb-2 ${
                    activeTab === "expired"
                      ? "text-[#8B313A] font-semibold border-b-2 border-[#8B313A]"
                      : "text-gray-500"
                  }`}
                >
                  Expired / no subscription
                </button>
              </nav>
            </div>

            <div className="p-6">
              <form
                className="relative flex items-center mb-6 gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  setAppliedSearch(searchInput.trim());
                }}
              >
                <FaSearch className="absolute left-3 text-gray-600 text-xl pointer-events-none" />
                <input
                  type="search"
                  value={searchInput}
                  onChange={(ev) => setSearchInput(ev.target.value)}
                  placeholder="Search cooperative name or email"
                  className="flex-1 border border-gray-300 rounded-md pl-10 pr-3 py-2 focus:ring-0 outline-none text-gray-700 w-full"
                />
                <button
                  type="submit"
                  className="bg-[#31778c] text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  Search
                </button>
              </form>

              {loadError && (
                <div className="text-red-600 text-sm mb-4">{loadError}</div>
              )}

              {!isLoadingList && overview.rows.length === 0 && (
                <p className="text-gray-600 text-sm py-8">
                  No cooperatives match this filter.
                </p>
              )}

              {isLoadingList ? (
                <CooperativeListSkeleton />
              ) : (
                <div className="divide-y divide-gray-200">
                  {overview.rows.map((row) => (
                    <div
                      key={row.company_id}
                      className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 py-5"
                    >
                      <div>
                        <p className="text-gray-900 font-semibold">{row.name}</p>
                        <p className="text-gray-500 text-sm">
                          {[row.city, row.branch_name].filter(Boolean).join(" · ") ||
                            row.address ||
                            "—"}
                        </p>
                        <p className="text-gray-400 text-xs mt-1">
                          Plan: {row.plan_name ?? "—"} · Members:{" "}
                          {row.members_count ?? 0}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[#19bc31] font-bold text-lg">
                          NGN {row.sms_balance_naira}
                        </p>
                        <p className="text-gray-500 text-sm">
                          SMS balance (primary branch)
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!isLoadingList && overview.lastPage > 1 && (
                <div className="flex justify-between items-center mt-6">
                  <button
                    type="button"
                    disabled={overview.currentPage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="text-sm text-[#31778c] disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {overview.currentPage} of {overview.lastPage}
                  </span>
                  <button
                    type="button"
                    disabled={overview.currentPage >= overview.lastPage}
                    onClick={() =>
                      setPage((p) =>
                        p >= overview.lastPage ? p : p + 1
                      )
                    }
                    className="text-sm text-[#31778c] disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <Outlet />
      )}
    </div>
  );
}
